import i18n from "i18next"

import { GraphDownloadError } from "@/api/api"

/**
 * Resolve a `downloadGraphFileApi` failure into a user-facing message.
 *
 * The backend rejects files it knows it cannot read — an encrypted or
 * password-protected Office file, detected by `graph_download_file`'s OLE2/CFBF
 * check — with a translation key and params rather than a generic failure, so
 * the user is told *why* and what to do about it. That only reaches them if the
 * caller actually reads `translationKey`; every caller that catches and shows a
 * hardcoded "download failed" toast throws the specific guidance away.
 *
 * `defaultValue` is the backend's own (English) text, so a key missing from a
 * locale degrades to a real sentence rather than to the raw key.
 *
 * Specificity order, most useful first. `downloadGraphFileApi` throws three
 * distinct things and each carries more information than the generic string, so
 * the generic is a last resort rather than the answer for everything that isn't
 * a translated rejection:
 *
 *   1. `GraphDownloadError` with `translationKey` — localized, actionable.
 *   2. `GraphDownloadError` with only `message` — the backend's `body.error`,
 *      written to be read by a user; falling through to the generic here would
 *      discard a specific reason the backend went to the trouble of sending.
 *   3. Any other `Error` — in practice `Failed to download file: {status}
 *      {statusText}`, which at least tells the user (and support) what failed.
 *   4. Nothing usable — the generic retry message.
 *
 * Step 3 matters beyond tidiness: the canvas image picker previously showed
 * `error.message` directly, so routing it through a helper that only handled
 * case 1 made canvas *less* informative than before it was refactored.
 */
export function graphDownloadErrorMessage(error: unknown): string {
  const graphError = error instanceof GraphDownloadError ? error : undefined
  if (graphError?.translationKey) {
    return i18n.t(graphError.translationKey, {
      ...graphError.translationParams,
      defaultValue: graphError.message,
    })
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  return i18n.t("filepicker.graph_download_error", {
    defaultValue: "Failed to download file. Please try again.",
  })
}
